import "datatables.net-dt/css/dataTables.dataTables.css";
import "./styles.css";

import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { buildTableFilterValues } from "./buildTableFilterValues";
import {
  IColumnDefinition,
  ITableActiveFilter,
  ITableFilter,
} from "./Interfaces";
import { ZeosFilterGroup } from "./ZeosFilterGroup";

// Register DataTables plugin
DataTable.use(DT);

interface IProps {
  unmappedData: any[];
  dataMapping: (unmappedData: any[]) => any[];
  columnDefs: IColumnDefinition[];
  searchable: boolean;
  pagination: boolean;
  initialOrder?: [number, "asc" | "desc"][];
  emptyMessage?: string;
  showFilters?: boolean;
  footerContent?: React.ReactNode;
  /** Column key → filter values that always appear as chips (grayed when unused). */
  filters?: Record<string, string[]>;
}
export const ZeosTable = (props: IProps) => {
  const [tableData, setTableData] = useState<any[]>(props.unmappedData);
  const [tableFilters, setTableFilters] = useState<ITableFilter[]>();
  const [activeFilters, setActiveFilters] = useState<ITableActiveFilter[]>([]);
  const [footerTarget, setFooterTarget] = useState<HTMLDivElement | null>(null);

  // Keep local rows in sync when parent data changes (e.g. property switch).
  useEffect(() => {
    setTableData(props.unmappedData);
  }, [props.unmappedData]);

  // Builds filter chips: configured filter values + distinct values from current data.
  useEffect(() => {
    const getFilters: ITableFilter[] = props.columnDefs.flatMap(
      (column, index) => {
        if (!column.filterable) {
          return [];
        }

        const uniqueValues = buildTableFilterValues(
          props.unmappedData.map((item) => item[column.key]),
          props.filters?.[column.key] ?? []
        );

        return uniqueValues.map((value) => ({
          columnIndex: index,
          value,
          setActive: (clickedFilter: ITableActiveFilter) =>
            setActiveFilters((prev) => {
              if (
                prev.filter(
                  (filter) => filter.filterKey == clickedFilter.filterKey
                ).length == 0
              )
                return [...prev, clickedFilter];
              else
                return prev.filter(
                  (filter) => filter.filterKey != clickedFilter.filterKey
                );
            }),
        }));
      }
    );
    setTableFilters(getFilters);
    setActiveFilters((prev) =>
      prev.filter((active) => {
        const stillListed = getFilters.some(
          (filter) =>
            filter.value === active.value &&
            filter.columnIndex === active.columnIndex
        );
        if (!stillListed) {
          return false;
        }

        const column = props.columnDefs[active.columnIndex];
        if (!column) {
          return false;
        }

        return props.unmappedData.some(
          (item) => String(item[column.key] ?? "").trim() === active.value
        );
      })
    );
  }, [props.unmappedData, props.columnDefs, props.filters]);

  const data = props.dataMapping(tableData.filter((x) => x.key != -1));

  // Ref to access DataTable instance directly
  const tableRef = useRef<any>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  // Handle checkbox state toggle and update local table state
  const handleToggleHide = (key: number) => {
    setTableData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, hideFlag: !item.hideFlag } : item
      )
    );
  };

  // Attach a manual event listener to checkboxes in the table
  useEffect(() => {
    const table = tableRef.current?.dt();
    if (!table) return;

    // Update state when any checkbox is toggled
    table.on("change", 'input[type="checkbox"]', function (e: any) {
      const key = Number(e.target.dataset.key);
      handleToggleHide(key);
    });

    // Cleanup event listener when component unmounts
    return () => {
      table?.off("change");
    };
  }, [tableData]);

  useEffect(() => {
    if (!props.footerContent) {
      setFooterTarget(null);
      return;
    }

    let retryId: number | null = null;
    let secondRetryId: number | null = null;

    if (!attachFooterTarget()) {
      retryId = window.setTimeout(() => {
        if (!attachFooterTarget()) {
          secondRetryId = window.setTimeout(() => {
            attachFooterTarget();
          }, 50);
        }
      }, 0);
    }

    return () => {
      if (retryId) {
        window.clearTimeout(retryId);
      }
      if (secondRetryId) {
        window.clearTimeout(secondRetryId);
      }
      if (footerRef.current?.parentElement) {
        footerRef.current.parentElement.removeChild(footerRef.current);
      }
      footerRef.current = null;
    };
  }, [props.footerContent, tableData]);

  // Handles filtering
  useEffect(() => {
    const tableApi = tableRef.current?.dt();
    if (tableApi) {
      const totalColumns = tableApi.columns().indexes().length;
      for (let i = 0; i < totalColumns; i++) {
        tableApi.column(i).search("");
      }

      const grouped: { [columnIndex: number]: string[] } = {};
      for (const filter of activeFilters) {
        if (!grouped[filter.columnIndex]) {
          grouped[filter.columnIndex] = [];
        }
        grouped[filter.columnIndex].push(filter.value);
      }
      for (const columnIndexStr in grouped) {
        const columnIndex = Number(columnIndexStr);
        const values = grouped[columnIndex];

        if (values.length > 0) {
          const regexPattern = values.map((v) => `^${v}$`).join("|");
          tableApi.column(columnIndex).search(regexPattern, true, false);
        }
      }

      tableApi.draw();
    }
  }, [activeFilters, tableData]);

  const showFilters = props.showFilters ?? true;

  const attachFooterTarget = () => {
    const table = tableRef.current?.dt();
    const container = table?.table?.().container?.() as HTMLElement | undefined;
    if (!container) {
      return false;
    }

    const paging = container.querySelector(".dt-paging");
    const pagingRow = paging?.closest(".dt-layout-row");
    const bottomRow =
      pagingRow ||
      container.querySelector(".dt-layout-row.dt-layout-bottom") ||
      container.querySelector(".dt-layout-bottom") ||
      container.querySelector(".dt-layout-row:last-child");
    if (!bottomRow || !bottomRow.parentElement) {
      return false;
    }

    let target = container.querySelector(".zeos-table-footer") as HTMLDivElement | null;
    if (!target) {
      target = document.createElement("div");
      target.className = "zeos-table-footer";
      bottomRow.parentElement.insertBefore(target, bottomRow);
    }
    footerRef.current = target;
    setFooterTarget(target);
    syncFooterColumnWidths();
    return true;
  };

  const syncFooterColumnWidths = () => {
    const table = tableRef.current?.dt();
    const container = table?.table?.().container?.() as HTMLElement | undefined;
    if (!container) {
      return;
    }
    const headerCells = Array.from(
      container.querySelectorAll("thead th")
    ).filter((cell) => window.getComputedStyle(cell).display !== "none");
    const footerCols = Array.from(
      container.querySelectorAll(".zeos-table-footer-col")
    ) as HTMLElement[];
    if (headerCells.length === 0 || footerCols.length === 0) {
      return;
    }
    const totalWidth = headerCells.reduce(
      (sum, cell) => sum + cell.getBoundingClientRect().width,
      0
    );
    footerCols.forEach((col, index) => {
      const headerCell = headerCells[index];
      if (!headerCell || totalWidth <= 0) {
        return;
      }
      const width = headerCell.getBoundingClientRect().width;
      col.style.width = `${(width / totalWidth) * 100}%`;
    });
  };

  return (
    <div>
      {showFilters && (
        <ZeosFilterGroup
          data={data}
          filters={tableFilters}
          activeFilters={activeFilters}
        />
      )}
      <DataTable
        ref={tableRef}
        data={data}
        columns={props.columnDefs}
        className="display w-full p-4 bg-white shadow-sm"
        options={{
          pageLength: 25,
          order: props.initialOrder,
          pagingType: "simple_numbers",
          layout: {
            topStart: null,
            topEnd: props.searchable ? "search" : null,
            bottomStart: props.pagination
              ? {
                  info: {
                    callback: function (settings, start, end, max, total, pre) {
                      return `Showing ${total} Total Incentive Program Matches`;
                    },
                  },
                }
              : null,
            bottomEnd: props.pagination ? ["pageLength", "paging"] : null,
          },
          language: {
            lengthMenu: "Records to show_MENU_",
            paginate: {
              previous: "Prev",
              next: "Next",
            },
            emptyTable:
              props.emptyMessage ||
              "No incentives identified for this property. Programs change frequently, check back periodically.",
          },
          lengthMenu: [5, 10, 25],
          headerCallback: function (thead) {
            Array.from(thead.querySelectorAll("th")).forEach((cell) => {
              cell.classList.add(
                "border-l-[1px]",
                "border-r-[1px]",
                "border-t-[1px]",
                "border-b-[1px]",
                "border-[#0000004d]",
                "bg-blue-100",
                "text-left",
                "uppercase",
                "border-separate"
              );
            });
          },
          createdRow: function (row) {
            Array.from(row.cells).forEach((cell) => {
              cell.classList.add(
                "border-l-[1px]",
                "border-r-[1px]",
                "border-t-[1px]",
                "border-b-[1px]",
                "border-[#0000004d]",
                "bg-zeos-white",
                "!shadow-none",
                "border-separate"
              );
            });
          },
          initComplete: function () {
            if (props.footerContent) {
              attachFooterTarget();
              syncFooterColumnWidths();
            }
          },
          drawCallback: function () {
            if (props.footerContent) {
              attachFooterTarget();
              syncFooterColumnWidths();
            }
          },
        }}
      />
      {footerTarget && props.footerContent
        ? createPortal(props.footerContent, footerTarget)
        : null}
    </div>
  );
};
